import { Capacitor } from '@capacitor/core';
import { useRef, useState, useEffect } from 'react';
import { CapacitorUpdater } from '@capgo/capacitor-updater';

import { withTimeout, TimeoutError } from 'src/utils/promise-timeout';
import { capacitorUpdaterService } from 'src/utils/Services/capacitor-updater.service';

import { initializeAuthListener } from 'src/auth/store/firebase/auth-store';

import { useSnackbar } from 'src/components/snackbar';

// ----------------------------------------------------------------------

// Timeout configuration (in milliseconds)
// Note: These values are aligned with capacitor.config.ts settings
// Plugin appReadyTimeout is 30s, so guard timeout should be less
const TIMEOUTS = {
  NOTIFY_APP_READY: 25000,      // 25 seconds for notifyAppReady (less than plugin's 30s)
  GET_CURRENT_BUNDLE: 5000,     // 5 seconds for getting current bundle
  VERSION_CHECK: 5000,          // 5 seconds for version comparison
  RESET_TO_BUILTIN: 15000,      // 15 seconds for reset operation
  ABSOLUTE_MAX: 35000,          // 35 seconds absolute maximum (safety net beyond plugin timeout)
} as const;

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

export default function AppUpdaterGuard({ children }: Props) {
  const [isReady, setIsReady] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const absoluteTimeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    const initializeUpdater = async () => {
      // Only run on native platforms
      if (!Capacitor.isNativePlatform()) {
        setIsReady(true);
        return;
      }

      // Add small delay to ensure Capacitor bridge is ready
      // This prevents semaphore timeout (0ms) race condition
      // The bridge needs a moment to initialize after app launch
      await new Promise(resolve => setTimeout(resolve, 100));

      // Set absolute timeout as a safety net
      // If initialization takes longer than 30 seconds, force the app to load
      absoluteTimeoutRef.current = setTimeout(() => {
        console.warn('AppUpdaterGuard: Absolute timeout reached (30s) - forcing app to load');
        setIsReady(true);
        enqueueSnackbar('App initialization took too long. Loading app anyway.', {
          variant: 'warning',
        });
      }, TIMEOUTS.ABSOLUTE_MAX);

      try {
        // Notify the plugin that the app is ready
        // This must be called within the timeout specified in capacitor.config.ts
        await withTimeout(
          CapacitorUpdater.notifyAppReady(),
          TIMEOUTS.NOTIFY_APP_READY,
          'notifyAppReady timeout'
        );

        // Initialize auth listener now that Firebase and Capacitor are ready
        // This prevents race conditions where auth tries to access Firebase before it's initialized
        // Using console.error to ensure visibility in console capture
        console.error('=========================================');
        console.error('[AppUpdaterGuard] CALLING initializeAuthListener()');
        console.error('=========================================');
        initializeAuthListener();
        console.error('[AppUpdaterGuard] initializeAuthListener() completed');

        // Get current bundle information
        const currentBundle = await withTimeout(
          CapacitorUpdater.current(),
          TIMEOUTS.GET_CURRENT_BUNDLE,
          'getCurrentBundle timeout'
        );
        console.log('Current bundle:', currentBundle);

        // Check if native version should be used instead of OTA bundle
        // This happens when a new app store version is installed that is >= the OTA version
        const versionCheck = await withTimeout(
          capacitorUpdaterService.shouldUseNativeVersion(),
          TIMEOUTS.VERSION_CHECK,
          'versionCheck timeout'
        );

        console.log('Version check result:', {
          nativeVersion: versionCheck.nativeVersion,
          bundleVersion: versionCheck.bundleVersion,
          isRunningNative: versionCheck.isRunningNative,
          shouldReset: versionCheck.shouldReset,
        });

        if (versionCheck.shouldReset) {
          console.log(
            `Native version (${versionCheck.nativeVersion}) is >= bundle version (${versionCheck.bundleVersion}). Resetting to native version...`
          );

          // Reset to native built-in version
          // This will reload the app with the native version
          await withTimeout(
            capacitorUpdaterService.resetToBuiltin(),
            TIMEOUTS.RESET_TO_BUILTIN,
            'resetToBuiltin timeout'
          );

          // The app will reload after reset, so we won't reach here
          // But we set isReady to false to show loading state
          setIsReady(false);
          return;
        }

        console.log('Using current bundle version:', versionCheck.bundleVersion);

        // Check if we need to handle auto-rollback
        // This happens if the previous update failed and was rolled back
        const failedBundle = localStorage.getItem('capacitor-updater-failed-bundle');
        if (failedBundle) {
          enqueueSnackbar('Previous update was rolled back due to startup issues', {
            variant: 'warning',
          });
          localStorage.removeItem('capacitor-updater-failed-bundle');
        }

        // Clear absolute timeout since we completed successfully
        if (absoluteTimeoutRef.current) {
          clearTimeout(absoluteTimeoutRef.current);
        }

        setIsReady(true);
      } catch (error) {
        console.error('Failed to initialize Capacitor Updater:', error);

        // Clear absolute timeout
        if (absoluteTimeoutRef.current) {
          clearTimeout(absoluteTimeoutRef.current);
        }

        // Check if this was a timeout error
        if (error instanceof TimeoutError) {
          console.warn(`Updater initialization timed out: ${error.message}`);
          enqueueSnackbar(
            'App updater initialization timed out. Loading app anyway.',
            { variant: 'warning' }
          );
        } else {
          // Store failed bundle info for rollback detection
          // Check for rollback-related errors using multiple indicators
          if (error instanceof Error) {
            const errorMessage = error.message.toLowerCase();
            const isRollbackError =
              errorMessage.includes('rollback') ||
              errorMessage.includes('update failed') ||
              errorMessage.includes('bundle failed');

            if (isRollbackError) {
              try {
                const currentBundle = await withTimeout(
                  CapacitorUpdater.current(),
                  TIMEOUTS.GET_CURRENT_BUNDLE,
                  'getCurrentBundle timeout in error handler'
                );
                localStorage.setItem(
                  'capacitor-updater-failed-bundle',
                  JSON.stringify(currentBundle)
                );
              } catch (bundleError) {
                console.error('Failed to store failed bundle info:', bundleError);
              }
            }
          }

          enqueueSnackbar('App updater initialization failed. Updates may not work properly.', {
            variant: 'error',
          });
        }

        // App can still continue even if updater initialization fails
        setIsReady(true);
      }
    };

    initializeUpdater();

    // Cleanup function
    return () => {
      if (absoluteTimeoutRef.current) {
        clearTimeout(absoluteTimeoutRef.current);
      }
    };
  }, [enqueueSnackbar]);

  // Show loading state while initializing
  if (!isReady && Capacitor.isNativePlatform()) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        {/* This loading state should be very brief */}
        <div>Initializing app...</div>
      </div>
    );
  }

  return <>{children}</>;
}
